import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OneWayInRecommendation, OneWayInService, OneWayInOpportunityStatusCreateDto } from 'src/api/v2';
import { useAuth } from 'src/features/authorization';
import {
  ONE_WAY_IN_ALL_SAVED_FORMS_KEY,
  ONE_WAY_IN_FEEDBACK,
  ONE_WAY_IN_FORM_FULL_HISTORY,
  ONE_WAY_IN_GROUPED_FORM,
  ONE_WAY_IN_OPPORTUNITY_BY_ID,
  ONE_WAY_IN_ACTORS_WITH_ACCESS,
} from 'src/constants/hook-keys';

const useOneWayInMultiple = (formIds: string[], enabled: boolean) => {
  const [oneWayInService] = useState(new OneWayInService());
  const { checkAccess } = useAuth();
  const { read, write } = checkAccess('OneWayIn');
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();

  const oneWayInGroupedFormsWOEnrollmentsQuery = useQueries({
    queries: formIds.map((id) => ({
      queryKey: [ONE_WAY_IN_GROUPED_FORM, id],
      queryFn: () => oneWayInService.getInquiry(id),
      enabled: !!id && read && enabled,
      retry: false,
      staleTime: 1000 * 60 * 15, // 15 min refetching on mount
      refetchOnMount: true,
    })),
  });

  const recommendService = useMutation({
    mutationFn: ({ recommendation }: { recommendation: OneWayInRecommendation; formId: string }) => {
      if (!write) {
        throw new Error(tCommon('cannotWrite'));
      }
      return oneWayInService.recommendServices(recommendation);
    },
    onSuccess: (_data, { formId }) => {
      queryClient.invalidateQueries([ONE_WAY_IN_FORM_FULL_HISTORY, formId]);
      queryClient.refetchQueries([ONE_WAY_IN_FEEDBACK, formId]);
      queryClient.invalidateQueries([ONE_WAY_IN_OPPORTUNITY_BY_ID, formId]);
      queryClient.invalidateQueries([ONE_WAY_IN_ACTORS_WITH_ACCESS, formId]);
    },
    retry: false,
  });

  const recommendServiceBatch = async (
    recommendations: Array<{ recommendation: OneWayInRecommendation; formId: string }>,
  ) => {
    const results = await Promise.all(recommendations.map((item) => recommendService.mutateAsync(item)));

    // Invalidate forms cache only once after all mutations complete
    queryClient.invalidateQueries([ONE_WAY_IN_ALL_SAVED_FORMS_KEY]);

    return results;
  };

  const deliverService = useMutation({
    mutationFn: ({ statusUpdate }: { statusUpdate: OneWayInOpportunityStatusCreateDto; formId: string }) => {
      if (!write) {
        throw new Error(tCommon('cannotWrite'));
      }
      return oneWayInService.putWayInOpportunityStatus(statusUpdate);
    },
    onSuccess: (_data, { formId }) => {
      queryClient.invalidateQueries([ONE_WAY_IN_FORM_FULL_HISTORY, formId]);
      queryClient.refetchQueries([ONE_WAY_IN_FEEDBACK, formId]);
      queryClient.invalidateQueries([ONE_WAY_IN_OPPORTUNITY_BY_ID, formId]);
      queryClient.invalidateQueries([ONE_WAY_IN_ACTORS_WITH_ACCESS, formId]);
    },
    retry: false,
  });

  const deliverServiceBatch = async (
    statusUpdates: Array<{ statusUpdate: OneWayInOpportunityStatusCreateDto; formId: string }>,
  ) => {
    const results = await Promise.all(statusUpdates.map((item) => deliverService.mutateAsync(item)));

    // Invalidate forms cache only once after all mutations complete
    queryClient.invalidateQueries([ONE_WAY_IN_ALL_SAVED_FORMS_KEY]);

    return results;
  };

  return {
    oneWayInGroupedFormsWOEnrollmentsQuery,
    recommendService,
    recommendServiceBatch,
    deliverService,
    deliverServiceBatch,
    formsLoading: oneWayInGroupedFormsWOEnrollmentsQuery.some((query) => query.isLoading),
  };
};

export default useOneWayInMultiple;
